import { clearLocalStorage } from "@faire/help-center-self-serve/utils/localStorage";
import { getBrandEmail } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerEmail } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getUserEmail } from "@faire/help-center-utils/serialized-data/getUser";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ICreateTicketRequest } from "@faire/web-api--source/com/faire/vendor/zendesk/ICreateTicketRequest";
import { ICreateTicketResponse } from "@faire/web-api--source/com/faire/vendor/zendesk/ICreateTicketResponse";
import createTicket from "@faire/web-api--source/endpoints/www/api/zendesk/create-ticket/post";
import { trackCxFoundationsTicketcreated } from "@faire/web-api--source/events/cxFoundations/actionCategoryOther/ticketcreated";
import { trackCxFoundationsEmailUsPageSubmitButtonClick } from "@faire/web-api--source/events/cxFoundations/click/emailUsPageSubmitButton";
import { trackCxFoundationsEmailUsPageView } from "@faire/web-api--source/events/cxFoundations/view/emailUsPage";
import { Formik } from "formik";
import React, { useState } from "react";

import { BackButton } from "../../components/BackButton";
import { useEmailFormState } from "../../hooks/useEmailFormState";
import { useScrollToTopOnLoadEffect } from "../../hooks/useScrollToTopEffect";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { LimitedWidthWrapper } from "../components/LimitedWidthWrapper";

import { TicketForm } from "./TicketForm";
import {
  getOrderTokenFromField,
  getTicketPayload,
  IEmailUsFormValues,
  uploadAttachmentIfAny,
  validateForm,
} from "./utils";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const EmailUsForm: React.FC = () => {
  useTrackPageViewEffect(trackCxFoundationsEmailUsPageView);
  useScrollToTopOnLoadEffect(10);

  const { strictLocalize } = useStrictLocalization();
  const [attachment, setAttachment] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    goToNextStep,
    selectedBrandOrder,
    issueType,
    retailerOrBrandToken,
    emailUsInitial,
  } = useEmailFormState();

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_email_us",
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageLoadKey: "help_center-page_self_serve_email_us",
    isPageLoading: false,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
  });

  const afterSubmission = () => {
    setIsLoading(false);
    clearLocalStorage();
    goToNextStep({ supportType: "email" });
  };

  const handleSubmit = async (values: IEmailUsFormValues) => {
    setIsLoading(true);
    try {
      const attachmentToken = await uploadAttachmentIfAny(attachment);
      const orderToken = getOrderTokenFromField(
        selectedBrandOrder,
        values.orderNumber
      );
      const { ticketPayload } = getTicketPayload({
        selectedBrandOrder,
        values,
        issueType,
        attachmentToken,
      });
      const response = (await createTicket(
        ICreateTicketRequest.build(ticketPayload)
      )) as ICreateTicketResponse;
      trackCxFoundationsTicketcreated(
        (response?.id ?? "").toString(),
        orderToken ?? "",
        retailerOrBrandToken
      );
      trackCxFoundationsEmailUsPageSubmitButtonClick(
        issueType,
        retailerOrBrandToken,
        getUserEmail() ?? getRetailerEmail() ?? getBrandEmail() ?? "",
        (response?.id ?? "").toString(),
        orderToken,
        values.name,
        values.subject,
        values.description,
        values.email
      );

      afterSubmission();
    } catch (err) {
      setErrorMessage(
        strictLocalize({
          defaultMessage: "Your request failed. Please retry again.",
        })
      );
      setIsLoading(false);
    }
  };

  return (
    <LimitedWidthWrapper ref={loadTimingRef}>
      <BackButton />
      <Spacer height="4x" />
      <Typography variant="sectionHeaderSansMedium">
        <StrictLocalMsg
          defaultMessage="Request help"
          description={{
            text: "Title for submit your feedback in email",
            img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg defaultMessage="Fill out this form, and we’ll reply to the email address below within 1 business day." />
      </Typography>
      <Spacer height="3x" />
      <Formik
        enableReinitialize
        initialValues={emailUsInitial}
        validate={(values) => validateForm(values, strictLocalize)}
        onSubmit={handleSubmit}
      >
        <TicketForm
          attachment={attachment}
          setAttachment={setAttachment}
          isLoading={isLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      </Formik>
    </LimitedWidthWrapper>
  );
};
